/**
 * web-forms.js — shared behaviour for the self-hosted form sections (DR-0052):
 * form-part-exchange, form-sell-piano, form-price-match, form-concert-hire,
 * form-finance-quote, form-job-application.
 *
 *  - conditional follow-up fields (data-wf-show-when="name=value")
 *  - file picker with type/size check + thumbnail preview (data-wf-files)
 *  - piano search over Shopify predictive search (data-wf-product-search)
 *  - repeatable rows (data-wf-repeat) for the application form
 *  - pop-up open/close when the section is set to "show as pop-up"
 *  - submit as multipart to the EC2 forms endpoint, then the thank-you state
 *
 * Nothing here posts to Stockmaster. The endpoint is the EC2 checkout host;
 * Stockmaster pulls from there on its own schedule.
 */
(function () {
  'use strict';

  var MAX_BYTES = 8 * 1024 * 1024;
  var MAX_FILES_TOTAL = 3;
  var IMAGE_PDF = { 'image/jpeg': 1, 'image/png': 1, 'image/webp': 1, 'image/heic': 1, 'image/heif': 1, 'image/gif': 1, 'application/pdf': 1 };
  var DOCS = { 'application/pdf': 1, 'application/msword': 1, 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 1 };
  var EXT_MIME = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', heic: 'image/heic', heif: 'image/heif', gif: 'image/gif', pdf: 'application/pdf', doc: 'application/msword', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' };

  function qsa(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function ext(name) { var p = String(name || '').toLowerCase().split('.'); return p.length > 1 ? p.pop() : ''; }
  function fmtBytes(n) { return n > 1048576 ? (n / 1048576).toFixed(1) + ' MB' : Math.round(n / 1024) + ' KB'; }

  // ── Conditional fields ───────────────────────────────────────────────────
  function initConditionals(form) {
    var conds = qsa(form, '[data-wf-show-when]');
    if (!conds.length) return;
    function evaluate() {
      conds.forEach(function (el) {
        var rule = el.getAttribute('data-wf-show-when').split('=');
        var name = rule[0], want = rule[1];
        var field = form.querySelector('[name="' + name + '"]:checked') || form.querySelector('select[name="' + name + '"], input[name="' + name + '"]:not([type=radio])');
        var val = field ? field.value : '';
        var show = val === want;
        el.hidden = !show;
        qsa(el, 'input, select, textarea').forEach(function (inp) {
          if (show) { if (inp.getAttribute('data-wf-required') === '1') inp.required = true; }
          else { if (inp.required) inp.setAttribute('data-wf-required', '1'); inp.required = false; }
          inp.disabled = !show;
        });
      });
    }
    form.addEventListener('change', evaluate);
    evaluate();
  }

  // ── Repeatable rows (application form) ───────────────────────────────────
  function initRepeats(form) {
    qsa(form, '[data-wf-repeat]').forEach(function (block) {
      var tpl = block.querySelector('template');
      var list = block.querySelector('[data-wf-repeat-list]');
      var add = block.querySelector('[data-wf-repeat-add]');
      var max = parseInt(block.getAttribute('data-wf-repeat-max'), 10) || 8;
      if (!tpl || !list || !add) return;
      function count() { return list.children.length; }
      function addRow() {
        if (count() >= max) return;
        var frag = tpl.content.cloneNode(true);
        var idx = count();
        qsa(frag, '[name]').forEach(function (inp) { inp.name = inp.name.replace('[]', '[' + idx + ']'); });
        var row = frag.querySelector('.wf-row') || frag.firstElementChild;
        var rm = frag.querySelector('[data-wf-repeat-remove]');
        if (rm) rm.addEventListener('click', function () { row.remove(); renumber(); add.disabled = count() >= max; });
        list.appendChild(frag);
        add.disabled = count() >= max;
      }
      function renumber() {
        qsa(list, '.wf-row').forEach(function (row, i) {
          qsa(row, '[name]').forEach(function (inp) { inp.name = inp.name.replace(/\[\d+\]/, '[' + i + ']'); });
        });
      }
      add.addEventListener('click', addRow);
      addRow();
    });
  }

  // ── Files ────────────────────────────────────────────────────────────────
  function initFiles(form, state) {
    qsa(form, '[data-wf-files]').forEach(function (box) {
      var key = box.getAttribute('data-wf-files');
      var input = box.querySelector('input[type=file]');
      var list = box.querySelector('.wf-files__list');
      var err = box.querySelector('.wf-files__error');
      var kind = box.getAttribute('data-wf-kind') === 'docs' ? DOCS : IMAGE_PDF;
      var max = parseInt(box.getAttribute('data-wf-max'), 10) || 1;
      state.files[key] = [];
      function render() {
        list.innerHTML = '';
        state.files[key].forEach(function (f, i) {
          var li = document.createElement('li');
          var isImg = /^image\//.test(f.type);
          li.innerHTML = (isImg ? '<img alt="" src="' + URL.createObjectURL(f) + '">' : '<span class="wf-files__doc">' + esc(ext(f.name).toUpperCase()) + '</span>')
            + '<span class="wf-files__name">' + esc(f.name) + ' <small>' + fmtBytes(f.size) + '</small></span>'
            + '<button type="button" class="wf-files__remove" aria-label="Remove">&times;</button>';
          li.querySelector('button').addEventListener('click', function () { state.files[key].splice(i, 1); render(); });
          list.appendChild(li);
        });
        input.value = '';
        box.classList.toggle('is-full', state.files[key].length >= max);
      }
      input.addEventListener('change', function () {
        err.textContent = '';
        var total = 0; Object.keys(state.files).forEach(function (k) { total += state.files[k].length; });
        Array.prototype.forEach.call(input.files, function (f) {
          var mime = f.type || EXT_MIME[ext(f.name)] || '';
          if (!kind[mime] && !kind[EXT_MIME[ext(f.name)]]) { err.textContent = box.getAttribute('data-wf-type-msg') || 'That file type is not accepted.'; return; }
          if (f.size > MAX_BYTES) { err.textContent = 'Each file must be 8 MB or smaller.'; return; }
          if (state.files[key].length >= max) { err.textContent = 'You can attach up to ' + max + ' file' + (max > 1 ? 's' : '') + ' here.'; return; }
          if (total >= MAX_FILES_TOTAL) { err.textContent = 'Up to ' + MAX_FILES_TOTAL + ' files in total.'; return; }
          state.files[key].push(f); total++;
        });
        render();
      });
      render();
    });
  }

  function filesValid(form, state) {
    var ok = true;
    qsa(form, '[data-wf-files]').forEach(function (box) {
      var key = box.getAttribute('data-wf-files');
      var err = box.querySelector('.wf-files__error');
      if (box.getAttribute('data-wf-required') === '1' && !(state.files[key] && state.files[key].length)) {
        err.textContent = box.getAttribute('data-wf-required-msg') || 'Please attach a file.';
        if (ok) box.scrollIntoView({ behavior: 'smooth', block: 'center' });
        ok = false;
      }
    });
    return ok;
  }

  // ── Piano search (Shopify predictive search) ─────────────────────────────
  function initProductSearch(form) {
    qsa(form, '[data-wf-product-search]').forEach(function (box) {
      var input = box.querySelector('.wf-search__input');
      var results = box.querySelector('.wf-search__results');
      var chosen = box.querySelector('.wf-search__chosen');
      var hHandle = box.querySelector('input[name="product_handle"]');
      var hTitle = box.querySelector('input[name="product_title"]');
      var hVariant = box.querySelector('input[name="variant_id"]');
      var hVariantTitle = box.querySelector('input[name="variant_title"]');
      if (!input || !results || !chosen || !hHandle || !hTitle) return;

      var typeFilter = (box.getAttribute('data-wf-type-filter') || '').toLowerCase().split(',').map(function (s) { return s.trim(); }).filter(Boolean);
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var timer = null;
      var requestId = 0;
      var controller = null;
      var currentItems = [];
      var activeIndex = -1;
      var initialVariant = hVariant ? hVariant.value : '';
      var initialVariantTitle = hVariantTitle ? hVariantTitle.value : '';
      var initialHandle = hHandle.value;
      var productFetchId = 0;
      var inputId = input.id || 'wf-product-search-' + Math.random().toString(36).slice(2, 9);
      input.id = inputId;
      input.setAttribute('role', 'combobox');
      input.setAttribute('aria-autocomplete', 'list');
      input.setAttribute('aria-expanded', 'false');
      results.id = inputId + '-results';
      input.setAttribute('aria-controls', results.id);
      results.setAttribute('role', 'listbox');

      function closeResults() {
        results.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
        activeIndex = -1;
      }
      function openResults() {
        if (input.hidden || !results.children.length) return;
        results.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      }
      function cancelSearch() {
        clearTimeout(timer);
        requestId++;
        if (controller) controller.abort();
        controller = null;
      }
      function showChosen(title, img, handle) {
        cancelSearch();
        closeResults();
        results.innerHTML = '';
        chosen.innerHTML = '<span class="wf-search__chosen-image">' + (img ? '<img alt="" src="' + esc(img) + '">' : '<span aria-hidden="true">&#9835;</span>') + '</span>'
          + '<span class="wf-search__chosen-info"><small>Selected piano</small><strong>' + esc(title) + '</strong></span>'
          + '<button type="button" class="wf-search__clear">Change piano</button>';
        chosen.hidden = false;
        input.hidden = true;
        hHandle.value = handle || '';
        hTitle.value = title || '';
        chosen.insertAdjacentHTML('beforeend', '<div class="wf-search__variants" hidden><label for="' + esc(inputId) + '-variant">Choose a version</label><select id="' + esc(inputId) + '-variant" class="wf-search__variant-select"></select></div>');
      }
      // Condition codes are stock states, not customer-selectable options.
      function isCondition(value) {
        return /^(?:A1|A2|A3|A4|A5|C1|DIS|SO)(?:\s*[-–—:|]\s*.*)?$/i.test(String(value || '').trim());
      }
      function chooseUnderlying(variants, preferredId) {
        var preferred = variants.find(function (v) { return String(v.id) === String(preferredId); });
        if (preferred) return preferred;
        return variants.find(function (v) { return v.available && (v.options || []).some(function (o) { return /^A1$/i.test(o); }); })
          || variants.find(function (v) { return v.available; })
          || variants.find(function (v) { return (v.options || []).some(function (o) { return /^A1$/i.test(o); }); })
          || variants[0];
      }
      var variantGroups = {};
      function loadVariants(handle, preserveId) {
        var fetchId = ++productFetchId;
        var panel = chosen.querySelector('.wf-search__variants');
        var select = chosen.querySelector('.wf-search__variant-select');
        if (!panel || !select) return;
        panel.hidden = true;
        select.disabled = true;
        variantGroups = {};
        fetch(root + 'products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
          .then(function (r) { if (!r.ok) throw new Error('Unable to load variants'); return r.json(); })
          .then(function (product) {
            if (fetchId !== productFetchId || hHandle.value !== handle) return;
            var variants = product.variants || [];
            if (!variants.length) throw new Error('No variants found');
            var optionNames = product.options || [];
            var visibleIndices = optionNames.map(function (name, i) {
              if (/^(?:condition|stock condition|item condition|product condition)$/i.test(String(name).trim())) return -1;
              var values = variants.map(function (v) { return (v.options || [])[i]; }).filter(Boolean);
              if (values.length && values.every(isCondition)) return -1;
              return i;
            }).filter(function (i) { return i !== -1; });
            // Some imports use only 'Title': strip condition codes from slash-separated titles.
            function visibleParts(v) {
              if (visibleIndices.length) return visibleIndices.map(function (i) {
                return (v.options || [])[i] || '';
              }).join(' / ').split(/\s*\/\s*/).filter(function (value) {
                return value && value !== 'Default Title' && !isCondition(value);
              });
              return String(v.title || '').split(/\s*\/\s*/).filter(function (part) {
                return part && part !== 'Default Title' && !isCondition(part);
              });
            }
            var labels = [];
            variants.forEach(function (v) {
              var label = visibleParts(v).join(' / ');
              var key = label || '__default__';
              if (!variantGroups[key]) { variantGroups[key] = []; labels.push(key); }
              variantGroups[key].push(v);
            });
            var initiallySelected = variants.find(function (v) { return String(v.id) === String(preserveId); });
            var activeKey = initiallySelected ? (visibleParts(initiallySelected).join(' / ') || '__default__') : labels[0];
            function selectGroup(key, preferred) {
              var variantsInGroup = variantGroups[key] || variantGroups[labels[0]];
              var variant = chooseUnderlying(variantsInGroup, preferred);
              if (hVariant) hVariant.value = variant.id;
              // Save the actual Shopify variant title for reference, even when condition is hidden.
              if (hVariantTitle) hVariantTitle.value = variant.title === 'Default Title' ? '' : variant.title;
              select.value = key;
            }
            select.innerHTML = labels.map(function (key) {
              return '<option value="' + esc(key) + '">' + esc(key === '__default__' ? 'Standard' : key) + '</option>';
            }).join('');
            panel.hidden = labels.length <= 1;
            select.disabled = labels.length <= 1;
            selectGroup(activeKey, preserveId);
          })
          .catch(function () {
            if (fetchId !== productFetchId) return;
            panel.hidden = true;
            select.disabled = true;
            if (hVariant) hVariant.value = preserveId || '';
            if (hVariantTitle) hVariantTitle.value = initialVariantTitle || '';
          });
      }
      chosen.addEventListener('change', function (e) {
        if (!e.target.matches('.wf-search__variant-select')) return;
        var choices = variantGroups[e.target.value] || [];
        if (!choices.length) return;
        var variant = chooseUnderlying(choices, '');
        if (hVariant) hVariant.value = variant.id;
        if (hVariantTitle) hVariantTitle.value = variant.title === 'Default Title' ? '' : variant.title;
      });
      function clearChoice() {
        productFetchId++;
        cancelSearch();
        closeResults();
        chosen.hidden = true;
        chosen.innerHTML = '';
        input.hidden = false;
        input.value = '';
        results.innerHTML = '';
        hHandle.value = '';
        hTitle.value = '';
        if (hVariant) hVariant.value = '';
        if (hVariantTitle) hVariantTitle.value = '';
        input.focus();
      }
      chosen.addEventListener('click', function (e) {
        if (e.target.closest('.wf-search__clear')) clearChoice();
      });
      if (hHandle.value && hTitle.value) {
        showChosen(hTitle.value, box.getAttribute('data-wf-prefill-image'), hHandle.value);
        loadVariants(hHandle.value, initialVariant);
      }
      else { chosen.hidden = true; input.hidden = false; }

      function matches(p) {
        if (!typeFilter.length) return true;
        var tags = Array.isArray(p.tags) ? p.tags.join(' ') : (p.tags || '');
        var hay = ((p.product_type || '') + ' ' + tags + ' ' + (p.title || '')).toLowerCase();
        return typeFilter.some(function (t) { return hay.indexOf(t) !== -1; });
      }
      function message(msg) {
        currentItems = [];
        results.innerHTML = '<li class="wf-search__none" role="status">' + esc(msg) + '</li>';
        openResults();
      }
      function render(items) {
        currentItems = items;
        activeIndex = -1;
        results.innerHTML = items.map(function (p, i) {
          var img = (p.featured_image && p.featured_image.url) || p.image || '';
          return '<li role="option" id="' + inputId + '-option-' + i + '" aria-selected="false">'
            + '<button type="button" data-index="' + i + '">'
            + '<span class="wf-search__result-image">' + (img ? '<img loading="lazy" alt="" src="' + esc(img) + '">' : '<span aria-hidden="true">&#9835;</span>') + '</span>'
            + '<span class="wf-search__result-info"><strong>' + esc(p.title) + '</strong>' + (p.vendor ? '<small>' + esc(p.vendor) + '</small>' : '') + '</span>'
            + '<span class="wf-search__result-arrow" aria-hidden="true">&#8250;</span></button></li>';
        }).join('');
        openResults();
      }
      function selectItem(i) {
        var p = currentItems[i];
        if (!p) return;
        var img = (p.featured_image && p.featured_image.url) || p.image || '';
        showChosen(p.title, img, p.handle);
        if (hVariant) hVariant.value = '';
        if (hVariantTitle) hVariantTitle.value = '';
        loadVariants(p.handle, p.handle === initialHandle ? initialVariant : '');
      }
      function setActive(i) {
        var options = qsa(results, '[role="option"]');
        if (!options.length) return;
        activeIndex = (i + options.length) % options.length;
        options.forEach(function (el, j) { el.setAttribute('aria-selected', j === activeIndex ? 'true' : 'false'); });
        input.setAttribute('aria-activedescendant', options[activeIndex].id);
        options[activeIndex].scrollIntoView({ block: 'nearest' });
      }
      function search(q) {
        cancelSearch();
        if (q.length < 2) { results.innerHTML = ''; closeResults(); return; }
        var id = requestId;
        controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        message('Searching pianos…');
        var url = root + 'search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,vendor,tag';
        fetch(url, { credentials: 'same-origin', signal: controller ? controller.signal : undefined })
          .then(function (r) { if (!r.ok) throw new Error('Search unavailable'); return r.json(); })
          .then(function (data) {
            if (id !== requestId || input.hidden || input.value.trim() !== q) return;
            var items = ((data.resources && data.resources.results && data.resources.results.products) || []).filter(matches);
            if (items.length) render(items);
            else message('No matching pianos found. Try another search or describe the piano below.');
          })
          .catch(function (err) {
            if (id !== requestId || err.name === 'AbortError') return;
            message('Search is unavailable at the moment. Please try again.');
          });
      }
      results.addEventListener('click', function (e) {
        var btn = e.target.closest('button[data-index]');
        if (btn) selectItem(Number(btn.getAttribute('data-index')));
      });
      input.addEventListener('input', function () {
        cancelSearch();
        var q = input.value.trim();
        if (q.length < 2) { results.innerHTML = ''; closeResults(); return; }
        timer = setTimeout(function () { search(q); }, 250);
      });
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { closeResults(); return; }
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          if (results.hidden && results.children.length) openResults();
          if (currentItems.length) { e.preventDefault(); setActive(activeIndex + (e.key === 'ArrowDown' ? 1 : -1)); }
        }
        if (e.key === 'Enter' && !results.hidden && activeIndex >= 0) { e.preventDefault(); selectItem(activeIndex); }
      });
      input.addEventListener('focus', function () { if (input.value.trim().length >= 2 && results.children.length) openResults(); });
      document.addEventListener('click', function (e) { if (!box.contains(e.target)) closeResults(); });
    });
  }

  // ── Pop-up ───────────────────────────────────────────────────────────────
  function initPopup(section) {
    var trigger = section.querySelector('[data-wf-open]');
    var modal = section.querySelector('.wf-modal');
    if (!trigger || !modal) return;
    function open() { modal.hidden = false; document.body.classList.add('wf-modal-open'); var f = modal.querySelector('input:not([type=hidden]), select, textarea'); if (f) setTimeout(function () { f.focus(); }, 50); }
    function close() { modal.hidden = true; document.body.classList.remove('wf-modal-open'); }
    trigger.addEventListener('click', open);
    qsa(modal, '[data-wf-close]').forEach(function (b) { b.addEventListener('click', close); });
    modal.addEventListener('click', function (e) { if (e.target === modal) close(); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !modal.hidden) close(); });
  }

  // ── Product page prefill: keep the variant in step with the picker ───────
  function initVariantTracking(section) {
    var hVariant = section.querySelector('input[name="variant_id"]');
    var hVariantTitle = section.querySelector('input[name="variant_title"]');
    if (!hVariant || !section.hasAttribute('data-wf-on-product')) return;
    var originalHandle = section.querySelector('input[name="product_handle"]');
    var pageHandle = originalHandle ? originalHandle.value : '';
    var variants = [];
    try { variants = JSON.parse(section.getAttribute('data-wf-variants') || '[]'); } catch (_) { variants = []; }
    function sync() {
      if (originalHandle && originalHandle.value !== pageHandle) return;
      var m = /[?&]variant=(\d+)/.exec(window.location.search);
      if (!m) return;
      hVariant.value = m[1];
      var v = variants.filter(function (x) { return String(x.id) === m[1]; })[0];
      if (v && hVariantTitle) hVariantTitle.value = v.title;
      var picker = section.querySelector('.wf-search__variant-select');
      // The customer-facing dropdown groups condition variants; don't set its value to a raw variant ID.
      if (picker && v) {
        var visible = Array.from(picker.options).find(function (o) {
          return o.value === v.title.split(/\s*\/\s*/).filter(function (part) { return !/^(A1|A2|A3|A4|A5|C1|DIS|SO)$/i.test(part); }).join(' / ');
        });
        if (visible) picker.value = visible.value;
      }
    }
    sync();
    window.addEventListener('popstate', sync);
    var ps = history.pushState, rs = history.replaceState;
    history.pushState = function () { ps.apply(this, arguments); sync(); };
    history.replaceState = function () { rs.apply(this, arguments); sync(); };
  }

  // ── Collect answers ──────────────────────────────────────────────────────
  var CONTEXT = { form_key: 1, channel: 1, shop: 1, page_url: 1, product_handle: 1, product_title: 1, variant_id: 1, variant_title: 1, job_ref: 1, job_title: 1, website: 1 };
  function setPath(obj, path, value) {
    var parts = path.replace(/\]/g, '').split('[');
    var cur = obj;
    for (var i = 0; i < parts.length - 1; i++) {
      var k = parts[i], nextIsIndex = /^\d+$/.test(parts[i + 1]);
      if (cur[k] == null) cur[k] = nextIsIndex ? [] : {};
      cur = cur[k];
    }
    cur[parts[parts.length - 1]] = value;
  }
  function collect(form) {
    var answers = {};
    qsa(form, 'input[name], select[name], textarea[name]').forEach(function (el) {
      if (el.disabled || el.type === 'file' || CONTEXT[el.name]) return;
      if (el.type === 'radio' && !el.checked) return;
      if (el.type === 'checkbox') { setPath(answers, el.name, el.checked ? (el.value === 'on' ? 'Yes' : el.value) : ''); return; }
      setPath(answers, el.name, el.value.trim());
    });
    // Drop empty repeatable rows
    Object.keys(answers).forEach(function (k) {
      if (Array.isArray(answers[k])) {
        answers[k] = answers[k].filter(function (row) { return row && Object.keys(row).some(function (f) { return String(row[f] || '').trim(); }); });
      }
    });
    return answers;
  }

  // ── Submit ───────────────────────────────────────────────────────────────
  function initSubmit(section, form, state) {
    var button = form.querySelector('button[type=submit]');
    var errBox = form.querySelector('.wf-error');
    var success = section.querySelector('.wf-success');
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      errBox.hidden = true; errBox.textContent = '';
      if (!form.checkValidity()) { form.reportValidity(); return; }
      if (!filesValid(form, state)) return;
      var fd = new FormData();
      Object.keys(CONTEXT).forEach(function (k) { var el = form.querySelector('[name="' + k + '"]'); if (el) fd.append(k, el.value || ''); });
      fd.append('page_url', window.location.href);
      fd.append('answers', JSON.stringify(collect(form)));
      Object.keys(state.files).forEach(function (key) { state.files[key].forEach(function (f) { fd.append('file__' + key, f, f.name); }); });
      button.disabled = true; button.classList.add('is-busy');
      var original = button.innerHTML; button.innerHTML = '<span>Sending&hellip;</span>';
      fetch(section.getAttribute('data-wf-endpoint'), { method: 'POST', body: fd, mode: 'cors' })
        .then(function (r) { return r.json().catch(function () { return { success: false, error: 'Unexpected reply from the server.' }; }); })
        .then(function (data) {
          if (!data.success) throw new Error(data.error || 'Something went wrong.');
          form.hidden = true;
          if (success) { success.hidden = false; success.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
          if (window.dataLayer) window.dataLayer.push({ event: 'web_form_submitted', form_key: section.getAttribute('data-wf-key') });
        })
        .catch(function (err) {
          errBox.textContent = err.message || 'We could not send your form. Please try again, or email us.';
          errBox.hidden = false;
          button.disabled = false; button.classList.remove('is-busy'); button.innerHTML = original;
        });
    });
  }

  function init(section) {
    if (section.getAttribute('data-wf-ready') === '1') return;
    section.setAttribute('data-wf-ready', '1');
    var form = section.querySelector('form.wf-form');
    if (!form) return;
    var state = { files: {} };
    initPopup(section);
    initVariantTracking(section);
    initConditionals(form);
    initRepeats(form);
    initFiles(form, state);
    initProductSearch(form);
    initSubmit(section, form, state);
  }

  function initAll() { qsa(document, '[data-wf-key]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll); else initAll();
  // Theme editor re-renders sections
  document.addEventListener('shopify:section:load', function (e) { qsa(e.target, '[data-wf-key]').forEach(init); if (e.target.hasAttribute && e.target.hasAttribute('data-wf-key')) init(e.target); });
})();
